import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { FiEdit2, FiX } from 'react-icons/fi';
import { getHostDetails, updateEntryInstructionEmails } from '../../store/thunks/HostActions';

function EntryInstructionsEmailSettings({ host, setSuccessMessage, setErrorMessage }) {
  const dispatch = useDispatch();
  const { hid } = useParams();
  const [editing, setEditing] = useState(false);
  const [receiveEmails, setReceiveEmails] = useState(host?.receiveEntryInstructionsEmails ?? true);
  const [additionalEmails, setAdditionalEmails] = useState(host?.entryInstructionsAdditionalEmails || []);
  const [newEmail, setNewEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [suggestedEmails, setSuggestedEmails] = useState([]);

  useEffect(() => {
    if (host) {
      setReceiveEmails(host.receiveEntryInstructionsEmails ?? true);
      setAdditionalEmails(host.entryInstructionsAdditionalEmails || []);

      // Extract email suggestions from internal notes
      const notes = host.internalHostNotes || '';
      const regex = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
      const found = notes.match(regex) || [];
      const cleaned = Array.from(new Set(found.map((e) => e.trim().toLowerCase())));
      setSuggestedEmails(cleaned.filter((e) => !(host.entryInstructionsAdditionalEmails || []).includes(e)));
    }
  }, [host]);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const addEmail = (candidate) => {
    const trimmedEmail = String(candidate || '').trim().toLowerCase();

    if (!trimmedEmail) {
      setErrorMessage('Please enter an email address');
      return false;
    }

    if (!validateEmail(trimmedEmail)) {
      setErrorMessage('Please enter a valid email address');
      return false;
    }

    if (additionalEmails.includes(trimmedEmail)) {
      setErrorMessage('This email is already in the list');
      return false;
    }

    setAdditionalEmails((current) => [...current, trimmedEmail]);
    setNewEmail('');
    setErrorMessage(null);
    return true;
  };

  const handleAddEmail = () => addEmail(newEmail);

  const handleRemoveEmail = (emailToRemove) => {
    setAdditionalEmails(additionalEmails.filter((email) => email !== emailToRemove));
  };

  const handleCancel = () => {
    setEditing(false);
    setReceiveEmails(host?.receiveEntryInstructionsEmails ?? true);
    setAdditionalEmails(host?.entryInstructionsAdditionalEmails || []);
    setNewEmail('');
    setErrorMessage(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setErrorMessage(null);

      const response = await dispatch(updateEntryInstructionEmails({
        hid,
        receiveEntryInstructionsEmails: receiveEmails,
        entryInstructionsAdditionalEmails: additionalEmails,
      }));

      if (updateEntryInstructionEmails.fulfilled.match(response)) {
        setSuccessMessage('Email settings saved successfully');
        setEditing(false);
        // Refresh host details in place instead of reloading the page.
        dispatch(getHostDetails(hid));
      } else {
        const errorMsg = response.payload?.error || response.payload?.message || 'Failed to save email settings';
        setErrorMessage(errorMsg);
      }
    } catch (error) {
      console.error('Error saving email settings:', error);
      setErrorMessage('Failed to save email settings');
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    'h-9 w-full rounded-lg border border-zinc-200 bg-white px-3 text-sm text-zinc-900 outline-none transition focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100';

  return (
    <section className="rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-zinc-900">Entry instructions emails</h3>
          <p className="text-xs text-zinc-500">Who gets the automated request for entry instructions on new orders.</p>
        </div>
        {editing ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleCancel}
              disabled={saving}
              className="h-8 rounded-lg border border-zinc-200 bg-white px-3 text-xs font-medium text-zinc-700 transition hover:bg-zinc-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="h-8 rounded-lg bg-zinc-900 px-3 text-xs font-semibold text-white transition hover:bg-zinc-800 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-3 text-xs font-medium text-zinc-700 transition hover:bg-zinc-50"
          >
            <FiEdit2 className="h-3.5 w-3.5" />
            Edit
          </button>
        )}
      </div>

      <div className="mt-3 space-y-4">
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            id="receiveEmails"
            checked={receiveEmails}
            onChange={(e) => setReceiveEmails(e.target.checked)}
            disabled={!editing}
            className="mt-0.5 h-4 w-4 rounded border-zinc-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-60"
          />
          <span>
            <span className="block text-sm font-medium text-zinc-800">Send entry instructions emails</span>
            <span className="block text-xs text-zinc-500">Host receives the automated request when a new order comes in.</span>
          </span>
        </label>

        <div>
          <div className="flex items-baseline justify-between">
            <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Additional recipients</p>
            <span className="text-xs tabular-nums text-zinc-400">{additionalEmails.length}</span>
          </div>

          {additionalEmails.length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {additionalEmails.map((email) => (
                <span
                  key={email}
                  className="inline-flex items-center gap-1 rounded-full bg-zinc-100 py-1 pl-2.5 pr-1.5 text-xs text-zinc-700 ring-1 ring-zinc-200"
                >
                  {email}
                  {editing ? (
                    <button
                      type="button"
                      onClick={() => handleRemoveEmail(email)}
                      aria-label={`Remove ${email}`}
                      className="flex h-4 w-4 items-center justify-center rounded-full text-zinc-400 transition hover:bg-zinc-200 hover:text-zinc-700"
                    >
                      <FiX className="h-3 w-3" />
                    </button>
                  ) : null}
                </span>
              ))}
            </div>
          ) : (
            <p className="mt-2 text-xs text-zinc-400">No additional recipients.</p>
          )}

          {editing ? (
            <div className="mt-3 space-y-2">
              <div className="flex gap-2">
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddEmail();
                    }
                  }}
                  placeholder="team@example.com"
                  className={inputClass}
                />
                <button
                  type="button"
                  onClick={handleAddEmail}
                  className="h-9 shrink-0 rounded-lg border border-zinc-200 bg-white px-3 text-xs font-semibold text-zinc-700 transition hover:bg-zinc-50"
                >
                  Add
                </button>
              </div>

              {suggestedEmails.length > 0 ? (
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-xs text-zinc-500">From notes:</span>
                  {suggestedEmails.map((email) => (
                    <button
                      key={email}
                      type="button"
                      onClick={() => {
                        if (addEmail(email)) {
                          setSuggestedEmails((current) => current.filter((e) => e !== email));
                        }
                      }}
                      className="rounded-full border border-dashed border-zinc-300 bg-white px-2.5 py-1 text-xs text-zinc-700 transition hover:border-indigo-300 hover:bg-indigo-50"
                    >
                      + {email}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

export default EntryInstructionsEmailSettings;
